import {
  ApolloClient,
  ApolloLink,
  fromPromise,
  HttpLink,
  InMemoryCache,
  NormalizedCacheObject,
} from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import { OWN_LOGIN_EMAIL_REFUSAL } from "./apolloErrorReporting";
import { Auth } from "aws-amplify";
import { RedirectUri } from "./components/enumStrings";
import { setContext } from "@apollo/client/link/context";
import * as Sentry from "@sentry/react";
import { ErrorCodeEnum } from "./graphql/types";
import {
  getApolloErrorConsoleSummary,
  getApolloOperationReportContext,
  isApolloErrorHandledLocally,
  isMemberContactLoginsRefusal,
  isSessionAuthRefusal,
  createPageUnloadTracker,
  isNetworkErrorFromPageUnload,
} from "./apolloErrorReporting";

const pageUnloadTracker = createPageUnloadTracker(window);

const uri =
  process.env.NODE_ENV === "production"
    ? process.env.REACT_APP_PROD_API_URL
    : process.env.REACT_APP_DEV_API_URL;

// Amplify returns the cached session, or renews it when the access token has
// expired. It corrects for a browser clock that is ahead or behind, so a
// request never goes out with a token the API already treats as expired
// (Sentry CPA-ADMIN-CONSOLE-69/-10: "reason: token expired").
const getCurrentAccessToken = async (): Promise<string | undefined> => {
  try {
    const session = await Auth.currentSession();
    return session.getAccessToken().getJwtToken();
  } catch (e) {
    // No signed-in user (guest pages, after sign out): send no token.
    return undefined;
  }
};

const SESSION_RETRY_CONTEXT_KEY = "cmpSessionRenewRetried";

const getApolloClient = async (
  handleLogout: () => void
): Promise<ApolloClient<NormalizedCacheObject>> => {
  const errorLink = onError((e) => {
    const urlPath = window.location.pathname;
    // A 500 from the API can carry its message only on the network error.
    const firstErrorMessage =
      (e?.graphQLErrors || [])[0]?.message ||
      ((e?.networkError as { result?: { errors?: { message?: string }[] } } | undefined)
        ?.result?.errors || [])[0]?.message;
    const operationName = e.operation.operationName;
    console.error(
      "Caught Apollo Client Error",
      getApolloErrorConsoleSummary({
        graphQLErrorCount: e.graphQLErrors?.length || 0,
        hasNetworkError: Boolean(e.networkError),
        operationName,
      })
    );

    Sentry.withScope((scope) => {
      const { operation, graphQLErrors, networkError } = e;
      scope.setTransactionName(operationName);
      scope.setContext(
        "apolloGraphQLOperation",
        getApolloOperationReportContext({
          operationName,
          variables: operation.variables,
        })
      );

      graphQLErrors?.forEach((error) => {
        // An expected refusal is shown on the page; it is not a fault to alert on.
        if (isMemberContactLoginsRefusal(error.message)) return;
        // An expired sign-in is renewed or sent to sign in below.
        if (isSessionAuthRefusal(error.message)) return;
        Sentry.captureMessage(error.message, {
          level: "error",
          fingerprint: ["{{ default }}", "{{ transaction }}"],
          contexts: {
            apolloGraphQLError: {
              message: error.message,
              extensions: error.extensions,
            },
          },
        });
      });

      // A request cancelled because the page is unloading is not an API failure.
      if (
        networkError &&
        !isSessionAuthRefusal(firstErrorMessage) &&
        !isNetworkErrorFromPageUnload({
          networkError: networkError as { name?: string; statusCode?: unknown },
          pageIsUnloading: pageUnloadTracker.isPageUnloading(),
        })
      ) {
        Sentry.captureMessage(networkError.message, {
          level: "error",
          contexts: {
            apolloNetworkError: {
              message: networkError.message,
            },
          },
        });
      }
    });

    // Expired or invalid sign-in: renew the session and retry once. With no
    // session left, go to sign in instead of the general error page.
    if (isSessionAuthRefusal(firstErrorMessage)) {
      if (e.operation.getContext()[SESSION_RETRY_CONTEXT_KEY]) {
        if (urlPath !== RedirectUri.logout) handleLogout();
        return;
      }
      e.operation.setContext({ [SESSION_RETRY_CONTEXT_KEY]: true });
      return fromPromise(
        Auth.currentSession().then(
          () => true,
          () => false
        )
      ).flatMap((renewed) => {
        if (!renewed) {
          if (urlPath !== RedirectUri.logout) handleLogout();
          return fromPromise(new Promise<never>(() => undefined));
        }
        return e.forward(e.operation);
      });
    }

    // if blacklist ting
    if (
      (e?.graphQLErrors || [{ message: "" }])[0]?.message.includes(
        ErrorCodeEnum.DisallowAccessTokenWithUserAuthYoungerThanUtcMilli
      ) ||
      (e?.graphQLErrors || [{ message: "" }])[0]?.message.includes(
        ErrorCodeEnum.DisallowAccessTokenPermanently
      ) ||
      (e?.graphQLErrors || [{ message: "" }])[0]?.message.includes(
        ErrorCodeEnum.DisallowUserTypeAccess
      )
    ) {
      if (urlPath !== RedirectUri.logout) {
        handleLogout();
        return;
      }
    }

    // The calling screen shows these errors and must stay open.
    if (isApolloErrorHandledLocally(operationName, e.operation.getContext())) {
      return;
    }

    // A member cannot change the email of their own login; the profile page
    // shows this message and stays open.
    if (
      (e?.graphQLErrors || [])[0]?.message?.startsWith(OWN_LOGIN_EMAIL_REFUSAL)
    ) {
      return;
    }

    // The credential screens show this refusal and stay open.
    if (isMemberContactLoginsRefusal((e?.graphQLErrors || [])[0]?.message)) {
      return;
    }

    if (
      urlPath !== RedirectUri.errorGeneral &&
      urlPath !== RedirectUri.support // ignoring this to prevent checking for graphql errors on the support page
    ) {
      window.location.href = RedirectUri.errorGeneral;
    }
  });

  const authLink = setContext(async (operation, previousContext) => {
    const accessToken = await getCurrentAccessToken();

    let apiAuthorization = accessToken ? `Bearer ${accessToken}` : "";

    // attach access token for the manual requests made during setting a permanent password while using a temporary password
    if (
      previousContext &&
      previousContext?.customAccessTokenForSettingHasSignedUp &&
      previousContext?.customAccessTokenForSettingHasSignedUp?.length > 0
    ) {
      apiAuthorization = `Bearer ${previousContext.customAccessTokenForSettingHasSignedUp}`;
    }

    return {
      ...previousContext, // ensure previous context is passed along to sustain cache and all the other apollo graphql goodies (without passing this everything breaks)
      headers: {
        authorization: apiAuthorization,
      },
    };
  });

  const httpLink = new HttpLink({
    uri,
  });

  const link = ApolloLink.from([errorLink, authLink, httpLink]);

  const client = new ApolloClient({
    link,
    cache: new InMemoryCache(),
  });

  return client;
};

export default getApolloClient;
